import { Artefact, Document, ServiceKind } from "@bitory-gmbh/bsm";
import { RFC3339timestamp } from "@bitory-gmbh/nidavellir";
import { Address } from "@/services/backend/addresses/types/address";
import { Representative } from "@/services/backend/representatives/types/representative";
import {
  BranchReference,
  BranchReferencesBase,
} from "@/services/backend/documents/types/branch-references-base";
import { CompanyReferenceBase } from "@/services/backend/documents/types/company-reference-base";
import { CustomerReferenceBase } from "@/services/backend/documents/types/customer-reference-base";
import {
  ServiceObjectReference,
  ServiceObjectReferencesBase,
} from "@/services/backend/documents/types/service-object-references-base";
import { DocumentStateBase } from "@/services/backend/documents/types/state";
import {
  Reference,
  ReferencePatch,
} from "@/services/backend/documents/types/reference-number";

/** Mirrors `document.Base`, backend/core/document/domain/document/base.go. */
export type DocumentBase = Document &
  Artefact &
  DocumentStateBase &
  Reference &
  CompanyReferenceBase &
  BranchReferencesBase &
  CustomerReferenceBase &
  ServiceObjectReferencesBase & {
    serviceKind: ServiceKind;
  };

/** Mirrors `document.OfferBase`, backend/core/document/domain/document/offer.go. */
export type OfferBase = {
  date?: RFC3339timestamp;
  representative: Representative;
  title: string;
  subtitle: string;
  text: string;
};

/** Mirrors `document.BasePatch`, backend/core/document/domain/document/base.go. */
export type BasePatch = ReferencePatch & {
  companyAddress?: Address;
  customerId?: string;
  customerAddress?: Address;
  branches?: BranchReference[];
  serviceObjects?: ServiceObjectReference[];
};

/** Mirrors `document.OfferBasePatch`, backend/core/document/domain/document/offer.go. */
export type OfferBasePatch = {
  date?: RFC3339timestamp | null;
  representative?: Representative;
  title?: string;
  subtitle?: string;
  text?: string;
};

export function patchDocumentBase<T extends DocumentBase>(
  document: T,
  patch: BasePatch,
): T {
  return {
    ...document,
    referenceNumber: patch.referenceNumber ?? document.referenceNumber,
    companyAddress: patch.companyAddress ?? document.companyAddress,
    customerId: patch.customerId ?? document.customerId,
    customerAddress: patch.customerAddress ?? document.customerAddress,
    branches: patch.branches ?? document.branches,
    serviceObjects: patch.serviceObjects ?? document.serviceObjects,
  };
}

export function patchOfferBase<T extends OfferBase>(
  offer: T,
  patch: OfferBasePatch,
): T {
  let date = offer.date;
  if (patch.date !== undefined) {
    date = patch.date ?? undefined;
  }

  return {
    ...offer,
    date,
    representative: patch.representative ?? offer.representative,
    title: patch.title ?? offer.title,
    subtitle: patch.subtitle ?? offer.subtitle,
    text: patch.text ?? offer.text,
  };
}
